import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import toast from 'react-hot-toast';
import { ConnectionState } from 'livekit-client';
import CallSession from '../components/CallSession';
import useCallStream from '../hooks/useCallStream';
import useNow from '../hooks/useNow';
import { resolveBackendPath } from '../utils/backendUrl';
import { normalizeLivekitUrl } from '../utils/livekitUrl';
import { formatRelative } from '../utils/time';
import type { Call, CallEvent } from '../types';
import { useOperatorConfig } from '../hooks/useOperatorConfig';
import { useConnectionStatus } from '../hooks/useConnectionStatus';
import { useAuth } from '../hooks/useAuth';
import useBreakpoint from '../hooks/useBreakpoint';

interface SessionState {
  call: Call;
  token: string;
  expiresAt: string;
  serverUrl: string;
}

let audioContext: AudioContext | null = null;
function playNotificationTone() {
  try {
    audioContext = audioContext ?? new AudioContext();
    const ctx = audioContext;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(660, ctx.currentTime);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.6);
  } catch (error) {
    console.warn('Unable to play notification tone', error);
  }
}

export default function CallsPage() {
  const { config } = useOperatorConfig();
  const { session: authSession } = useAuth();
  const operatorToken = authSession?.token ?? '';
  const livekitUrl = useMemo(() => normalizeLivekitUrl(config.livekitUrl), [config.livekitUrl]);
  const { isMobile, isTablet } = useBreakpoint();
  const [calls, setCalls] = useState<Map<string, Call>>(() => new Map());
  const [joiningCallId, setJoiningCallId] = useState<string | null>(null);
  const [callSession, setCallSession] = useState<SessionState | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.Disconnected);
  const [dismissing, setDismissing] = useState<Set<string>>(() => new Set());
  const { setBackendStatus, setLivekitStatus } = useConnectionStatus();
  const now = useNow(1000);
  const streamUrl = useMemo(
    () => resolveBackendPath('/api/calls/stream', config.backendUrl),
    [config.backendUrl],
  );
  const resolveApi = useCallback(
    (path: string) => resolveBackendPath(path, config.backendUrl),
    [config.backendUrl],
  );
  const configWarnings = useMemo(() => {
    const items: string[] = [];
    if (!config.backendUrl.trim()) {
      items.push('Backend API URL is not set; using the current origin.');
    }
    if (!livekitUrl) {
      items.push('LiveKit server URL is required to connect to active calls.');
    }
    if (!operatorToken) {
      items.push('Sign in to the operator console to join or end calls.');
    }
    return items;
  }, [config.backendUrl, operatorToken, livekitUrl]);

  const sortedCalls = useMemo(() => {
    return Array.from(calls.values()).sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [calls]);

  useEffect(() => {
    const count = sortedCalls.length;
    const consoleTitle = 'A.R.E.S - Anesthesia Remote Emergency Supervision - Operator Console';
    document.title = count > 0 ? `(${count}) ${consoleTitle}` : consoleTitle;
  }, [sortedCalls]);

  const updateLivekitConnection = useCallback(
    (state: ConnectionState) => {
      setConnectionState(state);
      if (state === ConnectionState.Connected) {
        setLivekitStatus('connected');
      } else if (state === ConnectionState.Connecting || state === ConnectionState.Reconnecting) {
        setLivekitStatus('connecting');
      } else {
        setLivekitStatus('disconnected');
      }
    },
    [setLivekitStatus],
  );

  const handleStreamEvent = useCallback(
    (event: CallEvent) => {
      if (event.type === 'existing_call' || event.type === 'new_call') {
        const payload: Call = {
          ...event.data,
          displayName: event.data.displayName ?? 'M400',
          agentName: event.data.agentName ?? null,
          deploymentLocation: event.data.deploymentLocation ?? null,
          deploymentLocationId: event.data.deploymentLocationId ?? null,
        };
        setCalls((prev) => {
          const next = new Map(prev);
          const alreadyHad = next.has(payload.callId);
          next.set(payload.callId, payload);
          if (event.type === 'new_call' && !alreadyHad) {
            const subject = payload.agentName || payload.displayName || 'Caller';
            const locationSuffix = payload.deploymentLocation ? ` at ${payload.deploymentLocation}` : '';
            playNotificationTone();
            toast.success(`${subject}${locationSuffix} is requesting support`);
          }
          return next;
        });
      } else if (event.type === 'ended') {
        setCalls((prev) => {
          const next = new Map(prev);
          next.delete(event.data.callId);
          return next;
        });
        setDismissing((prev) => {
          const next = new Set(prev);
          next.delete(event.data.callId);
          return next;
        });
        if (callSession?.call.callId === event.data.callId) {
          toast('Call ended by device', { icon: '☎️' });
          setCallSession(null);
          updateLivekitConnection(ConnectionState.Disconnected);
        }
      }
    },
    [callSession, updateLivekitConnection],
  );

  useCallStream(handleStreamEvent, streamUrl, {
    onStatusChange: setBackendStatus,
  });

  const joinCall = useCallback(
    async (call: Call) => {
      if (!operatorToken) {
        toast.error('Sign in to the operator console before joining calls.');
        return;
      }
      if (!livekitUrl) {
        toast.error('LiveKit URL is not configured.');
        return;
      }
      setJoiningCallId(call.callId);
      updateLivekitConnection(ConnectionState.Connecting);
      try {
        const response = await fetch(resolveApi(`/api/calls/${call.callId}/token?role=agent`), {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${operatorToken}`,
          },
        });
        if (!response.ok) {
          const detail = await response.text();
          throw new Error(detail || 'Unable to fetch token');
        }
        const data = (await response.json()) as { token: string; roomName: string; expiresAt: string };
        setCallSession({ call, token: data.token, expiresAt: data.expiresAt, serverUrl: livekitUrl });
      } catch (error) {
        console.error(error);
        toast.error(error instanceof Error ? error.message : 'Failed to join call');
        updateLivekitConnection(ConnectionState.Disconnected);
      } finally {
        setJoiningCallId(null);
      }
    },
    [operatorToken, livekitUrl, resolveApi, updateLivekitConnection],
  );

  const leaveSession = useCallback(() => {
    setCallSession(null);
    updateLivekitConnection(ConnectionState.Disconnected);
  }, [updateLivekitConnection]);

  const dismissCall = useCallback(async (call: Call) => {
    if (!operatorToken) {
      toast.error('Sign in to the operator console before ending calls.');
      return;
    }
    setDismissing((prev) => {
      const next = new Set(prev);
      next.add(call.callId);
      return next;
    });
    try {
      const response = await fetch(resolveApi(`/api/calls/${call.callId}/end`), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${operatorToken}`,
        },
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(detail || 'Unable to dismiss call');
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to dismiss call');
    } finally {
      setDismissing((prev) => {
        const next = new Set(prev);
        next.delete(call.callId);
        return next;
      });
    }
  }, [operatorToken, resolveApi]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }
      if (event.key.toLowerCase() === 'j') {
        event.preventDefault();
        if (!callSession) {
          const [firstCall] = sortedCalls;
          if (firstCall) {
            void joinCall(firstCall);
          }
        }
      }
      if (event.key.toLowerCase() === 'l') {
        event.preventDefault();
        if (callSession) {
          leaveSession();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sortedCalls, callSession, joinCall, leaveSession]);

  const bottomPadding = `calc(env(safe-area-inset-bottom, 0) + ${isMobile ? '1.75rem' : '1.5rem'})`;

  const layoutStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'minmax(300px, 360px) minmax(0, 1fr)' : 'minmax(320px, 420px) minmax(0, 1fr)',
    gap: isMobile ? '1.25rem' : '2rem',
    padding: isMobile
      ? `1rem 1.25rem ${bottomPadding}`
      : isTablet
        ? `1.25rem 1.75rem ${bottomPadding}`
        : `1.5rem 2rem ${bottomPadding}`,
    alignItems: 'start',
  };

  const warningsStyle: CSSProperties = {
    gridColumn: '1 / -1',
    borderRadius: isMobile ? '1rem' : '1.25rem',
    border: '1px solid rgba(234, 179, 8, 0.45)',
    background: 'rgba(234, 179, 8, 0.12)',
    padding: isMobile ? '1rem 1.1rem' : '1.25rem 1.5rem',
    color: '#facc15',
    boxShadow: '0 25px 50px -30px rgba(250, 204, 21, 0.35)',
  };

  const callListSectionStyle: CSSProperties = {
    background: 'rgba(15, 23, 42, 0.65)',
    borderRadius: isMobile ? '1.1rem' : '1.5rem',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    padding: isMobile ? '1.25rem 1.25rem 1.5rem' : '1.5rem',
    boxShadow: '0 30px 60px -30px rgba(30, 64, 175, 0.65)',
    position: isMobile ? 'static' : 'sticky',
    top: isMobile ? undefined : '6.5rem',
    maxHeight: isMobile ? undefined : 'calc(100vh - 8rem)',
    overflowY: isMobile ? 'visible' : 'auto',
  };

  const callsHeaderStyle: CSSProperties = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    gap: isMobile ? '0.75rem' : 0,
    marginBottom: '1rem',
  };

  const cardButtonRowStyle: CSSProperties = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    gap: '0.75rem',
  };

  const liveSessionSectionStyle: CSSProperties = {
    background: 'rgba(15, 23, 42, 0.55)',
    borderRadius: isMobile ? '1.1rem' : '1.5rem',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    minHeight: isMobile ? '420px' : '520px',
    boxShadow: '0 30px 70px -35px rgba(30, 64, 175, 0.7)',
    position: 'relative',
    overflow: 'hidden',
  };

  const liveSessionHeaderStyle: CSSProperties = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'space-between',
    gap: isMobile ? '0.75rem' : 0,
    padding: isMobile ? '1rem 1.25rem' : '1.25rem 1.75rem',
    borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
  };

  const liveSessionBodyStyle: CSSProperties = {
    height: 'calc(100% - 4.5rem)',
    position: 'relative',
  };

  return (
    <div style={layoutStyle}>
      {configWarnings.length > 0 && (
        <div style={warningsStyle}>
          <div style={{ fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.85rem' }}>
            Configuration Required
          </div>
          <ul style={{ margin: '0.75rem 0 0', paddingLeft: '1.25rem', color: '#fef3c7', lineHeight: 1.6 }}>
            {configWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      <section style={callListSectionStyle}>
        <header style={callsHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Incoming Calls</h2>
            <p style={{ margin: '0.25rem 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>
              {sortedCalls.length === 0
                ? 'Waiting for devices to request support'
                : `${sortedCalls.length} call${sortedCalls.length > 1 ? 's' : ''} in queue`}
            </p>
          </div>
          <kbd
            style={{
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '0.5rem',
              padding: '0.35rem 0.6rem',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              color: '#94a3b8',
              textTransform: 'uppercase',
              display: isMobile ? 'none' : 'inline-flex',
            }}
          >
            J → Join
          </kbd>
        </header>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {sortedCalls.length === 0 && (
            <div
              style={{
                borderRadius: '1rem',
                border: '1px dashed rgba(148, 163, 184, 0.25)',
                padding: '1.5rem',
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: '0.95rem',
              }}
            >
              Calls from devices will appear here automatically.
            </div>
          )}
          {sortedCalls.map((call) => {
            const age = formatRelative(new Date(call.createdAt), now);
            const isJoining = joiningCallId === call.callId;
            const isDismissing = dismissing.has(call.callId);
            const isActiveSession = callSession?.call.callId === call.callId;
            const headerLabel = call.agentName?.trim() || call.displayName?.trim() || 'M400';
            const showDeviceLabel =
              Boolean(call.agentName?.trim()) &&
              Boolean(call.displayName?.trim()) &&
              call.displayName?.trim() !== call.agentName?.trim();
            return (
              <article
                key={call.callId}
                style={{
                  borderRadius: '1.2rem',
                  border: isActiveSession
                    ? '1px solid rgba(34, 197, 94, 0.55)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                  padding: '1.25rem',
                  background: isActiveSession
                    ? 'rgba(22, 163, 74, 0.15)'
                    : 'rgba(15, 23, 42, 0.55)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  transition: 'border-color 0.2s ease, background 0.2s ease',
                }}
              >
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 600 }}>{headerLabel}</h3>
                    <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{age}</span>
                  </div>
                  {showDeviceLabel && (
                    <p style={{ margin: '0.35rem 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                      Device label: {call.displayName}
                    </p>
                  )}
                  <p style={{ margin: showDeviceLabel ? '0.2rem 0 0' : '0.35rem 0 0', color: '#64748b', fontSize: '0.85rem' }}>
                    Device ID: {call.deviceId}
                  </p>
                  {call.agentName && (
                    <p style={{ margin: '0.2rem 0 0', color: '#475569', fontSize: '0.8rem' }}>Agent: {call.agentName}</p>
                  )}
                  {call.deploymentLocation && (
                    <p style={{ margin: '0.2rem 0 0', color: '#475569', fontSize: '0.8rem' }}>
                      Deployment: {call.deploymentLocation}
                    </p>
                  )}
                  <p style={{ margin: '0.2rem 0 0', color: '#475569', fontSize: '0.8rem' }}>Room: {call.roomName}</p>
                </div>
                <div style={cardButtonRowStyle}>
                  <button
                    onClick={() => void joinCall(call)}
                    disabled={isJoining || isActiveSession}
                    style={{
                      flex: isMobile ? 'unset' : 1,
                      border: 'none',
                      borderRadius: '9999px',
                      padding: '0.75rem 1rem',
                      background: isActiveSession ? 'rgba(34, 197, 94, 0.35)' : '#2563eb',
                      color: 'white',
                      fontWeight: 600,
                      cursor: isJoining || isActiveSession ? 'not-allowed' : 'pointer',
                      letterSpacing: '0.02em',
                      boxShadow: isActiveSession
                        ? '0 20px 40px -20px rgba(34, 197, 94, 0.6)'
                        : '0 20px 40px -20px rgba(37, 99, 235, 0.8)',
                      transition: 'background 0.2s ease, transform 0.2s ease',
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    {isActiveSession ? 'In Call' : isJoining ? 'Connecting…' : 'Join'}
                  </button>
                  <button
                    onClick={() => void dismissCall(call)}
                    disabled={isDismissing}
                    style={{
                      borderRadius: '9999px',
                      border: '1px solid rgba(239, 68, 68, 0.6)',
                      padding: '0.75rem 1rem',
                      background: 'transparent',
                      color: '#f87171',
                      fontWeight: 600,
                      letterSpacing: '0.03em',
                      cursor: isDismissing ? 'not-allowed' : 'pointer',
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    {isDismissing ? 'Ending…' : 'Dismiss'}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <section style={liveSessionSectionStyle}>
        <header style={liveSessionHeaderStyle}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Live Session</h2>
            <p style={{ margin: '0.35rem 0 0', color: '#94a3b8', fontSize: '0.9rem' }}>
              {callSession ? `Room ${callSession.call.roomName}` : 'Join a call to start assisting'}
            </p>
          </div>
          <kbd
            style={{
              border: '1px solid rgba(148, 163, 184, 0.3)',
              borderRadius: '0.5rem',
              padding: '0.35rem 0.6rem',
              fontSize: '0.75rem',
              letterSpacing: '0.1em',
              color: '#94a3b8',
              textTransform: 'uppercase',
              alignSelf: isMobile ? 'flex-start' : 'center',
              display: isMobile ? 'none' : 'inline-flex',
            }}
          >
            L → Leave
          </kbd>
        </header>
        <div style={liveSessionBodyStyle}>
          {callSession ? (
            <CallSession
              call={callSession.call}
              token={callSession.token}
              serverUrl={callSession.serverUrl}
              onLeave={leaveSession}
              onConnectionStateChange={updateLivekitConnection}
            />
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '1rem',
                color: '#94a3b8',
                textAlign: 'center',
                padding: '0 3rem',
              }}
            >
              <div style={{ fontSize: '2.5rem' }}>📞</div>
              <p style={{ fontSize: '1.1rem', margin: 0 }}>Waiting to join a call</p>
              <p style={{ fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>
                You will see remote video here once you join a device room. Use the Join button or press the J key when a call
                arrives.
              </p>
            </div>
          )}
          {callSession && connectionState === ConnectionState.Reconnecting && (
            <div
              style={{
                position: 'absolute',
                bottom: '1.5rem',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'rgba(30, 64, 175, 0.85)',
                padding: '0.6rem 1.4rem',
                borderRadius: '9999px',
                color: 'white',
                fontWeight: 600,
                boxShadow: '0 20px 35px -20px rgba(30, 64, 175, 0.8)',
              }}
            >
              Attempting to reconnect…
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
